import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  Eye,
  KeyRound,
  LogOut,
  MessageCircle,
  Phone,
  RefreshCcw,
  Mail as MailIcon,
  Building2,
  Send,
  ShieldAlert,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ChangePasswordModal from '@/components/ChangePasswordModal';
import {
  adminForceNotify,
  adminListSubmissions,
  adminLogout,
  adminMe,
  billDownloadUrl,
  getAdminToken,
} from '@/lib/api';
import { formatINR, formatKWh } from '@/lib/format';

const NOTIFY_TO_WHATSAPP = '918154070298';

function StatusBadge({ status }) {
  const map = {
    ready: { label: 'Ready', className: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    extracting: { label: 'Extracting', className: 'bg-sky-100 text-sky-700 border-sky-200' },
    analyzing: { label: 'Analyzing', className: 'bg-sky-100 text-sky-700 border-sky-200' },
    queued: { label: 'Queued', className: 'bg-slate-100 text-slate-700 border-slate-200' },
    failed: { label: 'Failed', className: 'bg-rose-100 text-rose-700 border-rose-200' },
  };
  const meta = map[status] || { label: status || '—', className: 'bg-slate-100 text-slate-700' };
  return (
    <Badge variant="outline" className={`${meta.className} border font-medium`}>
      {meta.label}
    </Badge>
  );
}

function NotifyBadge({ status }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const map = {
    sent: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    sending: 'bg-sky-100 text-sky-700 border-sky-200',
    failed: 'bg-rose-100 text-rose-700 border-rose-200',
    skipped: 'bg-amber-100 text-amber-700 border-amber-200',
  };
  return (
    <Badge variant="outline" className={`${map[status] || 'bg-slate-100 text-slate-700'} border font-medium text-xs`}>
      {status}
    </Badge>
  );
}

function buildWhatsAppLink(sub) {
  const cust = sub.customer || {};
  const a = sub.analysis || {};
  const sol = a.solar || {};
  const bess = a.bess || {};
  const lines = [
    `UrjaPod — New Bill Analyzer Lead`,
    `Contact: ${cust.person_name || '-'}`,
    `Company: ${cust.company_name || '-'}`,
    `Email: ${cust.email || '-'}`,
    `Phone: ${cust.contact_number || '-'}`,
    `State: ${a.state || '-'} · ${a.consumer_type || '-'}`,
    `Solar: ${sol.kwp_dc || 0} kWp DC / ${sol.kw_ac || 0} kW AC`,
    `Battery: ${bess.kwh_capacity || 0} kWh`,
    `Annual savings: ₹ ${(a.total_annual_savings_rs || 0).toLocaleString('en-IN')}`,
  ];
  const text = encodeURIComponent(lines.join('\n'));
  return `https://wa.me/${NOTIFY_TO_WHATSAPP}?text=${text}`;
}

export default function AdminDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [total, setTotal] = useState(0);
  const [showChangePw, setShowChangePw] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminListSubmissions(200);
      setSubmissions(data.items || []);
      setTotal(data.count || 0);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('Session expired, please sign in again');
        navigate('/admin/login');
      } else {
        toast.error(err?.response?.data?.detail || 'Failed to load submissions');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getAdminToken()) {
      navigate('/admin/login');
      return;
    }
    (async () => {
      try {
        await adminMe();
        loadData();
      } catch {
        navigate('/admin/login');
      }
    })();
  }, [navigate, loadData]);

  const handleLogout = async () => {
    await adminLogout();
    toast.success('Signed out');
    navigate('/admin/login');
  };

  const handleForceNotify = async (jobId) => {
    try {
      await adminForceNotify(jobId);
      toast.success('Notification scheduled');
      setTimeout(loadData, 2500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to trigger notification');
    }
  };

  const readyCount = submissions.filter((s) => s.status === 'ready').length;
  const submittedCount = submissions.filter((s) => s.customer).length;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-7xl flex-1 px-4 py-8 sm:px-6 lg:px-8" data-testid="admin-dashboard">
        <div className="mb-6 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
          <div>
            <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              <ShieldAlert className="h-3.5 w-3.5" /> Admin
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground sm:text-3xl">
              Bill analyzer submissions
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {total} submissions · {readyCount} analyses ready · {submittedCount} customer forms filled
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={loadData}
              data-testid="admin-refresh-button"
            >
              <RefreshCcw className="mr-2 h-4 w-4" /> Refresh
            </Button>
            <Button
              variant="outline"
              className="rounded-xl"
              onClick={() => setShowChangePw(true)}
              data-testid="admin-change-password-button"
            >
              <KeyRound className="mr-2 h-4 w-4" /> Change password
            </Button>
            <Button
              variant="outline"
              className="rounded-xl border-destructive/30 text-destructive hover:bg-destructive/5"
              onClick={handleLogout}
              data-testid="admin-logout-button"
            >
              <LogOut className="mr-2 h-4 w-4" /> Sign out
            </Button>
          </div>
        </div>

        <Card className="overflow-hidden rounded-2xl border-border/70 bg-card" data-testid="submissions-card">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>State / Type</TableHead>
                  <TableHead>Bill</TableHead>
                  <TableHead>Analysis</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notification</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                      Loading submissions…
                    </TableCell>
                  </TableRow>
                )}
                {!loading && submissions.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="py-12 text-center text-sm text-muted-foreground">
                      No submissions yet.
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  submissions.map((s) => {
                    const cust = s.customer || {};
                    const analysis = s.analysis || {};
                    const solar = analysis.solar || {};
                    const bess = analysis.bess || {};
                    return (
                      <TableRow key={s.job_id} data-testid={`submission-row-${s.job_id}`}>
                        <TableCell>
                          <div className="font-medium text-foreground">
                            <div className="flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                              {cust.company_name || <span className="text-muted-foreground">— not submitted</span>}
                            </div>
                          </div>
                          {cust.person_name && (
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              {cust.person_name}
                            </div>
                          )}
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {new Date(s.created_at).toLocaleString('en-IN')}
                          </div>
                        </TableCell>
                        <TableCell>
                          {cust.email && (
                            <div className="flex items-center gap-1.5 text-sm">
                              <MailIcon className="h-3.5 w-3.5 text-muted-foreground" />
                              <a href={`mailto:${cust.email}`} className="text-primary hover:underline">
                                {cust.email}
                              </a>
                            </div>
                          )}
                          {cust.contact_number && (
                            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Phone className="h-3 w-3" /> {cust.contact_number}
                            </div>
                          )}
                          {!cust.email && !cust.contact_number && (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{analysis.state || s.state || '—'}</div>
                          <div className="text-xs text-muted-foreground">
                            {analysis.consumer_type || s.consumer_type || ''}
                          </div>
                        </TableCell>
                        <TableCell>
                          <a
                            href={billDownloadUrl(s.bill_url)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                            data-testid={`download-bill-${s.job_id}`}
                          >
                            <Download className="h-3.5 w-3.5" />
                            {s.original_filename || 'download'}
                          </a>
                        </TableCell>
                        <TableCell>
                          {analysis.total_annual_savings_rs ? (
                            <div className="text-xs">
                              <div className="font-semibold text-foreground tabular">
                                {formatINR(analysis.total_annual_savings_rs, { compact: true, decimals: 1 })}/yr
                              </div>
                              <div className="text-muted-foreground tabular">
                                Solar {solar.kwp_dc || 0} kWp · BESS {bess.kwh_capacity || 0} kWh
                              </div>
                              <div className="text-muted-foreground tabular">
                                {formatKWh(analysis?.slot_kwh_month?.[analysis.solar_slot_number] || 0)} TOD-3
                              </div>
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={s.status} />
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1 text-xs">
                            <div>
                              <span className="text-muted-foreground">WA:</span>{' '}
                              <NotifyBadge status={s.whatsapp_status} />
                            </div>
                            <div>
                              <span className="text-muted-foreground">Email:</span>{' '}
                              <NotifyBadge status={s.email_status} />
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex flex-wrap justify-end gap-1.5">
                            <a
                              href={`/?job_id=${s.job_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-8 items-center gap-1 rounded-lg border border-border/70 bg-primary/10 px-2.5 text-xs font-medium text-primary hover:bg-primary/20"
                              data-testid={`view-analysis-${s.job_id}`}
                            >
                              <Eye className="h-3.5 w-3.5" /> View
                            </a>
                            <a
                              href={buildWhatsAppLink(s)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex h-8 items-center gap-1 rounded-lg border border-border/70 bg-emerald-50 px-2.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100"
                              data-testid={`whatsapp-share-${s.job_id}`}
                            >
                              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
                            </a>
                            <button
                              type="button"
                              onClick={() => handleForceNotify(s.job_id)}
                              disabled={!s.customer || s.status !== 'ready'}
                              className="inline-flex h-8 items-center gap-1 rounded-lg border border-border/70 bg-secondary px-2.5 text-xs font-medium text-foreground hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50"
                              data-testid={`force-notify-${s.job_id}`}
                            >
                              <Send className="h-3.5 w-3.5" /> Notify
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </div>
        </Card>

        <p className="mt-4 text-xs text-muted-foreground">
          Upload limit per IP: <span className="font-semibold">10</span>. Bill downloads open in a new tab.
        </p>
      </main>
      <ChangePasswordModal open={showChangePw} onOpenChange={setShowChangePw} onChanged={handleLogout} />
      <Footer />
    </div>
  );
}
